import { SiteSettings } from '../types';

interface StockImage {
    url: string;
    photographer: string;
    photographer_url: string;
}

// Lista de Stop Words (Palavras para remover da busca)
const STOP_WORDS = new Set([
    "a", "o", "as", "os", "um", "uma", "uns", "umas", 
    "de", "do", "da", "dos", "das", "em", "no", "na", "nos", "nas", 
    "por", "para", "com", "sem", "e", "ou", "que", "se", "como", "mas", 
    "foi", "ser", "estar", "ter", "fazer", "sao", "era", "pelo", "pela", 
    "sobre", "entre", "ate", "isso", "isto", "aquilo", "esse", "essa", 
    "este", "esta", "aquele", "aquela", "ele", "ela", "eles", "elas", 
    "qual", "quais", "quem", "onde", "quando", "quanto", "porque", "pois", 
    "entao", "logo", "ja", "mais", "menos", "muito", "pouco", "bom", 
    "mau", "bem", "mal", "melhor", "pior", "agora", "hoje", "ontem", 
    "amanha", "aqui", "ali", "la", "ca", "tarde", "cedo", "sempre", 
    "nunca", "jamais", "talvez", "sim", "nao", "toda", "todo", "todas", 
    "todos", "tudo", "nada", "cada", "outro", "outra", "outros", "outras", 
    "algum", "alguma", "alguns", "algumas", "nenhum", "nenhuma", "cujo", 
    "cuja", "cujos", "cujas", "você", "voce", "preciso", "precisa", "sucesso",
    "fazer", "ter", "sua", "seu", "seus", "suas", "pode", "podem", "deve", "devem"
]);

// Função para limpar a query e extrair apenas palavras-chave relevantes
const extractKeywords = (query: string): string => {
    // 1. Remove caracteres especiais e pontuação
    const cleanText = query.toLowerCase().replace(/[^\w\sÀ-ÿ]/g, " ");
    
    // 2. Divide em palavras
    const words = cleanText.split(/\s+/);
    
    // 3. Filtra stop words e palavras muito curtas (exceto siglas conhecidas)
    const keywords = words.filter(word => {
        if (word.length <= 2 && !['ia', 'ai', 'ux', 'ui', '3d', 'vr', 'ar'].includes(word)) return false;
        return !STOP_WORDS.has(word);
    });

    // 4. Retorna as palavras unidas ou a query original se não sobrar nada
    return keywords.length > 0 ? keywords.join(" ") : query;
};

export const searchStockImages = async (query: string, settings: SiteSettings, count: number = 1): Promise<StockImage[]> => {
    const provider = settings.stockImageProvider || 'pexels';
    const apiKey = provider === 'pexels' ? settings.pexelsApiKey : settings.pixabayApiKey;

    // Aplica a limpeza de palavras-chave
    const cleanedQuery = extractKeywords(query);
    
    // Truncate query to avoid API errors (Pixabay max 100 chars)
    const safeQuery = cleanedQuery.substring(0, 100);

    if (!apiKey) {
        // Fallback to Pollinations (Free AI) if no key is provided, acting as a "free stock" generator
        console.warn("No Stock API Key found. Using Pollinations fallback.");
        return Array(count).fill(0).map((_, i) => ({
            url: `https://image.pollinations.ai/prompt/${encodeURIComponent(safeQuery)}?width=720&height=1280&nologo=true&seed=${Math.random()}`,
            photographer: 'AI Generated (Free)',
            photographer_url: '#'
        }));
    }

    try {
        if (provider === 'pexels') {
            const response = await fetch(`https://api.pexels.com/v1/search?query=${encodeURIComponent(safeQuery)}&orientation=portrait&per_page=${count}`, {
                headers: { Authorization: apiKey }
            });
            if (!response.ok) throw new Error(`Pexels API Error: ${response.statusText}`);
            const data = await response.json();
            return (data.photos || []).map((p: any) => ({
                url: p.src.portrait,
                photographer: p.photographer,
                photographer_url: p.photographer_url
            }));
        } else {
            // Pixabay requires + instead of %20 for spaces sometimes, but encodeURIComponent usually works.
            // However, let's be safe with the query length.
            const response = await fetch(`https://pixabay.com/api/?key=${apiKey}&q=${encodeURIComponent(safeQuery)}&image_type=photo&orientation=vertical&per_page=${count}`);
            if (!response.ok) throw new Error(`Pixabay API Error: ${response.statusText}`);
            const data = await response.json();
            return (data.hits || []).map((p: any) => ({
                url: p.largeImageURL,
                photographer: p.user,
                photographer_url: `https://pixabay.com/users/${p.user}-${p.user_id}/`
            }));
        }
    } catch (error) {
        console.error("Stock Image Error:", error);
        // Fallback with truncated query
        return Array(count).fill(0).map((_, i) => ({
            url: `https://image.pollinations.ai/prompt/${encodeURIComponent(safeQuery)}?width=720&height=1280&nologo=true&seed=${Math.random()}`,
            photographer: 'Fallback AI',
            photographer_url: '#'
        }));
    }
};
